import { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Circle, X, Sparkles } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { motion, AnimatePresence } from "framer-motion";

interface Props {
  userId: string;
  hasTelegramLinked: boolean;
  hasWatchedLesson: boolean;
  hasEnrolled: boolean;
}

const STORAGE_KEY = "fitawrari_onboarding_dismissed";

const OnboardingChecklist = ({ userId, hasTelegramLinked, hasWatchedLesson, hasEnrolled }: Props) => {
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    setDismissed(localStorage.getItem(`${STORAGE_KEY}_${userId}`) === "1");
  }, [userId]);

  const steps = [
    { label: "Create your account", done: true },
    { label: "Enroll in a course", done: hasEnrolled },
    { label: "Link your Telegram for updates", done: hasTelegramLinked },
    { label: "Watch your first lesson", done: hasWatchedLesson },
  ];

  const completed = steps.filter((s) => s.done).length;
  const allDone = completed === steps.length;

  if (dismissed || allDone) return null;

  const handleDismiss = () => {
    localStorage.setItem(`${STORAGE_KEY}_${userId}`, "1");
    setDismissed(true);
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -8 }}
      >
        <Card className="mb-6 border-primary/30 bg-gradient-to-br from-primary/5 to-transparent">
          <CardContent className="p-4">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-primary" />
                <h3 className="font-semibold text-sm">
                  Get started ({completed}/{steps.length})
                </h3>
              </div>
              <button
                onClick={handleDismiss}
                className="text-muted-foreground hover:text-foreground"
                aria-label="Dismiss checklist"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="w-full h-1.5 bg-muted rounded-full overflow-hidden mb-3">
              <motion.div
                className="h-full bg-primary"
                initial={{ width: 0 }}
                animate={{ width: `${(completed / steps.length) * 100}%` }}
                transition={{ duration: 0.5 }}
              />
            </div>
            <ul className="space-y-1.5">
              {steps.map((s) => (
                <li key={s.label} className="flex items-center gap-2 text-sm">
                  {s.done ? (
                    <CheckCircle2 className="w-4 h-4 text-primary shrink-0" />
                  ) : (
                    <Circle className="w-4 h-4 text-muted-foreground shrink-0" />
                  )}
                  <span className={s.done ? "text-muted-foreground line-through" : "text-foreground"}>
                    {s.label}
                  </span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      </motion.div>
    </AnimatePresence>
  );
};

export default OnboardingChecklist;
